import { useMemo, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { fbt } from 'fbtee'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { useAuth } from '../../hooks/useAuth'
import { useGroupDetail } from '../../hooks/useGroupDetail'
import { useGroupPendingRequests } from '../../hooks/useGroupPendingRequests'
import { canUnlinkGroupEvent, isGroupAdmin } from '@shared/groupRoles'
import { useEventsByIds } from '../../hooks/useEventsByIds'
import { useUserProfilesWithLoading } from '../../hooks/useUserProfiles'
import { useFriendEventInterests } from '../../hooks/useFriendEventInterests'
import { useUserEventInterests } from '../../hooks/useUserEventInterests'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { interestPrompt } from '../../lib/guestPrompts'
import { useGroupInvites } from '../../hooks/useGroupInvites'
import { usePageLoadTrace } from '../../lib/perf'
import { useTrackView } from '../../hooks/useTrackView'
import type { GroupEntryPoint } from '../../lib/analyticsEvents'
import { clearPendingIntent } from '../../lib/pendingIntent'
import { peekPendingGroupJoin } from '../../lib/groupJoin'
import { PageLayout } from '../../components/PageLayout'
import { NotFoundState } from '../../components/NotFoundState'
import { ErrorState } from '../../components/ErrorState'
import { InviteMembersDialog } from './InviteMembersDialog'
import { GroupHeader, type GroupViewerRole } from './GroupHeader'
import { GroupSettingsButton } from './GroupSettingsButton'
import { GroupPlanSections } from './GroupPlanSections'
import { PendingRequestsSection } from './PendingRequestsSection'
import { useVisibleRequests } from './useVisibleRequests'
import { GroupViewNav, type GroupView } from './GroupViewNav'
import { GroupTotemsManager } from '../../components/totem/GroupTotemsManager'
import { JoinGroupDialog } from './JoinGroupDialog'
import { buildGroupStats } from './groupStats'
import { splitLinkedEvents } from './splitLinkedEvents'
import { useMinuteNow } from '../../hooks/useMinuteNow'

export default function GroupPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const location = useLocation()
  const { user } = useAuth()
  const {
    group, groupEvents, loading, groupLoading, eventsLoading, error,
    unlinkEvent, upgradeGuestToMember, setAdmin, removeMember, approveRequest, rejectRequest,
  } = useGroupDetail(groupId!, user?.uid ?? null)
  useDocumentTitle(group ? pageTitle(group.name) : null)
  const isMember = !!user && !!group?.memberIds.includes(user.uid)
  const isAdmin = !!group && isGroupAdmin(group, user?.uid)
  const { requests: pendingRequests } = useGroupPendingRequests(isMember ? groupId! : null)
  const eventIds = useMemo(
    () => [...new Set([...groupEvents.map(ge => ge.eventId), ...pendingRequests.map(r => r.eventId)])],
    [groupEvents, pendingRequests],
  )
  const { events, pending: eventsHookLoading } = useEventsByIds(eventIds)
  const visibleRequests = useVisibleRequests(group, groupEvents, pendingRequests, events)

  const [joinPromptDismissed, setJoinPromptDismissed] = useState(false)
  const [showInviteMembers, setShowInviteMembers] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const [view, setView] = useState<GroupView>(tabParam === 'members' || tabParam === 'totems' ? tabParam : 'events')
  function changeView(next: GroupView) {
    setView(next)
    setSearchParams(next === 'events' ? {} : { tab: next }, { replace: true, state: location.state })
  }

  const { profiles: memberProfiles, loading: memberProfilesLoading } = useUserProfilesWithLoading(group?.memberIds ?? [])
  const { invites: groupInvites, sendInvite } = useGroupInvites(isMember ? groupId ?? null : null)
  const { map: memberInterests } = useFriendEventInterests(group?.memberIds ?? [])
  const { eventStatuses, setEventInterest } = useUserEventInterests(user?.uid ?? null)
  const { guard, prompt } = useGuestGuard()
  const guardedSetEventInterest = guard(setEventInterest, interestPrompt('event_interest'))

  const now = useMinuteNow()
  const { upcoming, past } = useMemo(
    () => splitLinkedEvents(groupEvents, events, now),
    [groupEvents, events, now],
  )

  const isGuest = !user || user.isAnonymous
  const isPendingMember =
    !!user &&
    !user.isAnonymous &&
    !isMember &&
    (!!group?.guestIds?.includes(user.uid) || peekPendingGroupJoin()?.groupId === groupId)
  const role: GroupViewerRole = isGuest ? 'guest' : isMember ? 'member' : isPendingMember ? 'pending' : 'none'

  const entryPoint = (location.state as { entryPoint?: GroupEntryPoint } | null)?.entryPoint ?? 'direct'
  useTrackView('group_viewed', group && { group_id: group.id, member_count: group.memberIds.length, entry_point: entryPoint })
  usePageLoadTrace('group_page_to_content', !loading)

  if (error) return <ErrorState error={error} />
  if (!groupLoading && !group) {
    return (
      <NotFoundState
        message={<fbt desc="Message shown when the requested group is not found">Group not found.</fbt>}
      />
    )
  }

  const eventsSectionLoading = eventsLoading || (groupEvents.length > 0 && eventsHookLoading)
  const othersInterested = (eventId: string) => {
    const buckets = memberInterests.get(eventId)
    return [...(buckets?.going ?? []), ...(buckets?.interested ?? [])].some(uid => uid !== user?.uid)
  }

  const openInvitePrompt = () =>
    prompt({
      intent: 'join_group',
      title: group
        ? fbt('Join ' + fbt.param('groupName', group.name), 'Title of the sign-up dialog triggered from the group invite banner')
        : fbt('Join this group', 'Title of the sign-up dialog triggered from the group invite banner before the group has loaded'),
      description: fbt('Log in to join the group and share your picks with friends.', 'Body of the upsell dialog triggered from the group invite banner'),
      afterSignIn: { pathname: `/groups/${groupId}` },
    })

  async function handleJoin() {
    await upgradeGuestToMember()
    clearPendingIntent('groupJoin')
    setJoinPromptDismissed(true)
  }

  return (
    <PageLayout back={true}>
      <GroupHeader
        group={group}
        stats={buildGroupStats({
          memberCount: group?.memberIds.length ?? 0,
          upcomingCount: upcoming.length,
          pastCount: past.length,
        })}
        statsLoading={eventsSectionLoading}
        role={role}
        onInvite={() => setShowInviteMembers(true)}
        onGuestJoin={openInvitePrompt}
        onJoin={handleJoin}
        settings={group && <GroupSettingsButton groupId={group.id} />}
      />

      {group && (
        <GroupViewNav
          view={view}
          onChange={changeView}
          memberCount={group.memberIds.length}
          showTotems={isMember && !isGuest}
          pendingCount={isAdmin ? visibleRequests.length : 0}
        />
      )}

      {group && view === 'totems' && isMember && !isGuest && <GroupTotemsManager group={group} />}

      {group && view !== 'totems' && (
        <GroupPlanSections
          view={view}
          group={group}
          upcoming={upcoming}
          past={past}
          eventsLoading={eventsSectionLoading}
          memberProfiles={memberProfiles}
          profilesLoading={memberProfilesLoading}
          memberInterests={memberInterests}
          eventStatuses={eventStatuses}
          userId={user?.uid ?? null}
          isMember={isMember && !isGuest}
          onSetEventInterest={user ? (eventId, status) => guardedSetEventInterest(eventId, status) : undefined}
          onUnlinkEvent={unlinkEvent}
          canUnlink={ge => canUnlinkGroupEvent(group, ge, user?.uid, othersInterested(ge.eventId))}
          onSetAdmin={isAdmin ? (uid, make) => void setAdmin(uid, make).catch(() => {}) : undefined}
          onRemoveMember={isAdmin ? uid => void removeMember(uid).catch(() => {}) : undefined}
          pendingSection={
            // No skeleton: most groups never have a suggestion, so a placeholder would only flash.
            isMember && visibleRequests.length > 0 ? (
              <PendingRequestsSection
                requests={visibleRequests}
                profiles={memberProfiles}
                isAdmin={isAdmin}
                onApprove={approveRequest}
                onReject={rejectRequest}
              />
            ) : undefined
          }
        />
      )}

      {group && (
        <InviteMembersDialog
          open={showInviteMembers}
          onOpenChange={setShowInviteMembers}
          group={group}
          invites={groupInvites}
          onSendInvite={sendInvite}
        />
      )}

      {group && (
        <JoinGroupDialog
          open={isPendingMember && !joinPromptDismissed}
          groupName={group.name}
          onDismiss={() => setJoinPromptDismissed(true)}
          onJoin={handleJoin}
        />
      )}
    </PageLayout>
  )
}
